import React, { useCallback, useMemo } from "react";
import { classArr } from "../helper/style-helper";
import * as styles from "./time-picker.module.scss";

const SECONDS_PER_DAY = 24 * 60 * 60;

/**
 * Converts a number of seconds to a time string in the format "HH:MM".
 */
function formatTimeValue(value: number): string {
	const secondsSinceMidnight =
		((Math.trunc(value) % SECONDS_PER_DAY) + SECONDS_PER_DAY) %
		SECONDS_PER_DAY;
	const hours = Math.floor(secondsSinceMidnight / 3600);
	const minutes = Math.floor((secondsSinceMidnight % 3600) / 60);

	return [hours, minutes]
		.map((part) => String(part).padStart(2, "0"))
		.join(":");
}

/**
 * Converts a time string in the format "HH:MM" to a number of seconds.
 * Returns null if the value is not a valid time string.
 */
function parseTimeValue(value: string): number | null {
	const [hours, minutes] = value.split(":");
	const parsedHours = Number(hours);
	const parsedMinutes = Number(minutes);

	if (Number.isNaN(parsedHours) || Number.isNaN(parsedMinutes)) {
		return null;
	}

	return parsedHours * 3600 + parsedMinutes * 60;
}

export interface TimePickerProps {
	value: number;
	onChange: (date: number) => void;
	invalid?: boolean;
}

export default function TimePicker(props: TimePickerProps) {
	const { onChange: propsOnChange } = props;

	const valueString = useMemo(
		() => formatTimeValue(props.value),
		[props.value],
	);
	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const nextValue = parseTimeValue(event.target.value);

			if (nextValue !== null) {
				propsOnChange(nextValue);
			}
		},
		[propsOnChange],
	);

	return (
		<input
			className={classArr(styles.input, props.invalid && styles.invalid)}
			type="time"
			value={valueString}
			onChange={handleChange}
			aria-invalid={props.invalid}
		/>
	);
}
